import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import SignedImage from "./SignedImage";
import { normalizeRouteStepTarget, storyRouteLinkProps } from "./ctaTargets";
import { COLORS } from "../../theme/tokens";

function PathBadge({ label, accent }) {
  const isOrange = accent === "orange";
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 62,
        height: 62,
        mt: "-44px",
        borderRadius: "50%",
        backgroundColor: isOrange ? COLORS.orange : COLORS.blue,
        color: isOrange ? "#203033" : "#fff",
        border: `6px solid ${COLORS.paper}`,
        boxShadow: isOrange ? COLORS.badgeOrangeShadow : COLORS.badgeBlueShadow,
        display: "grid",
        placeItems: "center",
        fontFamily: "windroos, Georgia, serif",
        fontSize: "1.2rem",
        lineHeight: 1,
      }}
    >
      {label}
    </Box>
  );
}

function PathStop({ block, stepNumber, mdMarginTop }) {
  const c = block.content || {};
  const accent = stepNumber % 2 === 1 ? "orange" : "blue";
  const badgeLabel = c.badge || String(stepNumber);
  const detailRoute = normalizeRouteStepTarget(c.detailRoute, c.badge);

  return (
    <Box
      sx={{
        position: "relative",
        zIndex: 1,
        mt: { xs: 0, md: mdMarginTop },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <PathBadge label={badgeLabel} accent={accent} />
      <Stack
        spacing={1.75}
        sx={{
          width: "100%",
          mt: -0.5,
          p: 2.5,
          borderRadius: 1.5,
          backgroundColor: "rgba(255, 255, 255, 0.96)",
          border: `1px solid ${COLORS.line}`,
          boxShadow: COLORS.shadowSoft,
        }}
      >
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontFamily: "windroos, Georgia, serif",
            color: COLORS.blue,
            fontSize: { xs: "1.4rem", md: "1.55rem" },
            lineHeight: 1.1,
            m: 0,
          }}
        >
          {c.heading}
        </Typography>
        {c.summary && (
          <Typography variant="body2" sx={{ color: "#304346", m: 0 }}>
            {c.summary}
          </Typography>
        )}
        {c.s3Key && (
          <Box
            sx={{
              aspectRatio: "4 / 3",
              borderRadius: 1.5,
              overflow: "hidden",
              border: `1px solid ${COLORS.line}`,
            }}
          >
            <SignedImage s3Key={c.s3Key} alt={c.imageAlt || c.heading} />
          </Box>
        )}
        {c.ctaLabel && detailRoute && (
          <Box>
            <Button
              component={Link}
              {...storyRouteLinkProps(detailRoute)}
              size="small"
              sx={{
                color: COLORS.red,
                borderColor: "rgba(209, 77, 24, 0.32)",
                backgroundColor: "rgba(255, 250, 244, 0.8)",
                border: "1px solid rgba(209, 77, 24, 0.32)",
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": {
                  backgroundColor: "rgba(255, 250, 244, 1)",
                  borderColor: COLORS.red,
                },
              }}
            >
              {c.ctaLabel}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

const STAGGER_MT = [0, 72, 22, 72];

export default function RouteStepsSection({ section }) {
  const { content, blocks = [] } = section;
  const steps = blocks
    .filter((b) => b.blockType === "route-step")
    .sort((a, b) => a.order - b.order);

  return (
    <Box
      id="in-de-klas"
      component="section"
      sx={{
        py: "clamp(48px, 7vw, 92px)",
        px: "clamp(20px, 5vw, 72px)",
        backgroundColor: COLORS.paper,
        scrollMarginTop: { xs: 64, md: 80 },
      }}
    >
      <Container disableGutters sx={{ maxWidth: { md: 1160 } }} maxWidth={false}>
        <SectionHeader
          eyebrow={content.eyebrow}
          heading={content.heading}
          lead={content.lead}
          reviewStatus="controleer"
        />
        <Box
          sx={{
            position: "relative",
            mt: { xs: 4, md: 5 },
            pt: { md: "28px" },
            pb: { md: "18px" },
            pl: { xs: "18px", md: 0 },
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: "7%",
              right: "7%",
              top: "98px",
              height: "120px",
              borderTop: "6px dashed rgba(60, 143, 153, 0.42)",
              borderRadius: "50%",
              transform: "rotate(-2deg)",
              display: { xs: "none", md: "block" },
              zIndex: 0,
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: "10px",
              top: "16px",
              bottom: "8px",
              borderLeft: "5px dashed rgba(60, 143, 153, 0.42)",
              display: { xs: "block", md: "none" },
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              display: "grid",
              gap: { xs: "28px", md: "18px" },
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              alignItems: "start",
            }}
          >
            {steps.map((block, i) => (
              <PathStop
                key={block.id}
                block={block}
                stepNumber={i + 1}
                mdMarginTop={`${STAGGER_MT[i % STAGGER_MT.length]}px`}
              />
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
